import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Status } from './data';
import type { NodeLayout } from './layout';
import type { Theme } from './theme';
import { drawIcon, ICONS, type IconName } from './icons';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NodeSprite {
  container:   Container;
  graphics:    Graphics;
  text:        Text;
  layout:      NodeLayout;
  statusBadge: StatusBadge;
  descBadge:   DescBadge;
  statusBar:   StatusBar;
  actionBar:   ActionBar;
}

export interface StatusBar {
  container: Container;
  /** One ActionButton per STATUS_CYCLE entry, in the same order. */
  buttons:   ActionButton[];
}

export const ACTION_BUTTON_RADIUS = 14;

interface StatusBadge {
  container: Container;
  graphics:  Graphics; // background disk
  iconGfx:   Graphics; // lucide SVG
}

/** Small interactive badge on the circle's right edge — opens the description modal. */
export interface DescBadge {
  container: Container;
  graphics:  Graphics; // background disk
  iconGfx:   Graphics; // lucide SVG
}

export interface ActionBar {
  container:    Container;
  addButton:    ActionButton;
  rotateButton: ActionButton;
  deleteButton: ActionButton;
}

interface ActionButton {
  container: Container;
  graphics:  Graphics; // background disk
  iconGfx:   Graphics; // lucide SVG
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_RADIUS      = 11;
const STATUS_ICON_SIZE   = 14;
// Description badge — smaller than the status badge, on the circle's right edge.
const DESC_RADIUS        = 8;
const DESC_ICON_SIZE     = 10;
const ACTION_RADIUS      = 14;
const ACTION_ICON_SIZE   = 16;
const ACTION_GAP         = 10;
const ACTION_OFFSET_Y    = 22;

const STATUS_ICON: Record<Exclude<Status, 'todo'>, IconName> = {
  'in-progress': 'ellipsis',
  'done':        'check',
  'blocked':     'circleX',
};

// Like STATUS_ICON, but covers all four statuses — used by the status button
// (always visible when the node is selected, even when the badge is hidden).
export const STATUS_BUTTON_ICON: Record<Status, IconName> = {
  'todo':        'circle',
  'in-progress': 'ellipsis',
  'done':        'check',
  'blocked':     'circleX',
};

export const STATUS_CYCLE: readonly Status[] = ['todo', 'in-progress', 'done', 'blocked'];

export function nextStatus(current: Status): Status {
  const i = STATUS_CYCLE.indexOf(current);
  return STATUS_CYCLE[(i + 1) % STATUS_CYCLE.length] as Status;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nodeColor(theme: Theme, level: number): number {
  return theme.nodeColors[Math.min(level, theme.nodeColors.length - 1)];
}

function makeTextStyle(layout: NodeLayout, theme: Theme): TextStyle {
  const fontSize  = Math.max(10, Math.min(16, Math.round(layout.radius * 0.28)));
  const wrapWidth = (layout.radius - 11) * 2;

  return new TextStyle({
    fontFamily:    '"Rajdhani", "Inter", Arial, sans-serif',
    fontSize,
    fontWeight:    layout.level === 0 ? '700' : '600',
    fill:          theme.textColor,
    wordWrap:      true,
    wordWrapWidth: wrapWidth,
    align:         'center',
  });
}

// ─── Lines ───────────────────────────────────────────────────────────────────

/**
 * `highlightEdges` is a set of `"<parentId>|<childId>"` keys identifying edges
 * to draw with `theme.pulseColor` instead of `theme.lineColor`. All other
 * styling (width, alpha) is unchanged so the highlight is purely chromatic.
 */
export function drawLines(
  graphics:        Graphics,
  layouts:         Map<string, NodeLayout>,
  theme:           Theme,
  highlightEdges?: Set<string>,
): void {
  graphics.clear();

  for (const node of layouts.values()) {
    for (const childId of node.childIds) {
      const child = layouts.get(childId);
      if (!child) continue;

      const isHighlighted = highlightEdges?.has(`${node.id}|${childId}`) ?? false;
      const color = isHighlighted ? theme.pulseColor : theme.lineColor;

      graphics
        .moveTo(node.x, node.y)
        .lineTo(child.x, child.y)
        .stroke({ width: theme.lineWidth, color, alpha: theme.lineAlpha });
    }
  }
}

// ─── Circle ──────────────────────────────────────────────────────────────────

export function renderCircle(gfx: Graphics, layout: NodeLayout, theme: Theme): void {
  gfx.clear();
  gfx.circle(0, 0, layout.radius);
  gfx.fill({ color: nodeColor(theme, layout.level) });
}

// ─── Status badge (top-right of circle, on border) ───────────────────────────

function renderStatusBadge(badge: StatusBadge, layout: NodeLayout, theme: Theme): void {
  if (layout.status === 'todo') {
    badge.container.visible = false;
    return;
  }
  badge.container.visible = true;

  badge.graphics.clear();
  badge.graphics.circle(0, 0, STATUS_RADIUS);
  badge.graphics.fill({ color: theme.statusColors[layout.status] });
  badge.graphics.circle(0, 0, STATUS_RADIUS);
  badge.graphics.stroke({ width: 2, color: theme.statusBorderColor });

  drawIcon(badge.iconGfx, ICONS[STATUS_ICON[layout.status]], theme.actionIconColor, STATUS_ICON_SIZE, 3);
}

function createStatusBadge(layout: NodeLayout, theme: Theme): StatusBadge {
  const container = new Container();
  const a = -Math.PI / 4; // NE on the circle border
  container.x = Math.cos(a) * layout.radius;
  container.y = Math.sin(a) * layout.radius;

  const graphics = new Graphics();
  container.addChild(graphics);

  const iconGfx = new Graphics();
  container.addChild(iconGfx);

  const badge = { container, graphics, iconGfx };
  renderStatusBadge(badge, layout, theme);
  return badge;
}

// ─── Description badge (right of circle, on border, smaller than status) ──────

function renderDescBadge(badge: DescBadge, layout: NodeLayout, theme: Theme): void {
  badge.graphics.clear();
  badge.graphics.circle(0, 0, DESC_RADIUS);
  badge.graphics.fill({ color: theme.rotateColor });
  badge.graphics.circle(0, 0, DESC_RADIUS);
  badge.graphics.stroke({ width: 2, color: theme.statusBorderColor });

  drawIcon(badge.iconGfx, ICONS['fileText'], theme.actionIconColor, DESC_ICON_SIZE, 2.4);

  // Full opacity when a description exists, dimmed when empty (still clickable).
  badge.container.alpha = layout.description.trim().length > 0 ? 1 : 0.4;
}

function createDescBadge(layout: NodeLayout, theme: Theme): DescBadge {
  const container = new Container();
  container.x = layout.radius;  // east, on the circle border
  container.y = 0;
  container.eventMode = 'static';
  container.cursor    = 'pointer';

  const graphics = new Graphics();
  container.addChild(graphics);

  const iconGfx = new Graphics();
  container.addChild(iconGfx);

  const badge = { container, graphics, iconGfx };
  renderDescBadge(badge, layout, theme);
  return badge;
}

// ─── Action bar (two buttons below circle, hidden by default) ────────────────

function renderActionButton(btn: ActionButton, bgColor: number, iconName: IconName, theme: Theme): void {
  btn.graphics.clear();
  btn.graphics.circle(0, 0, ACTION_RADIUS);
  btn.graphics.fill({ color: bgColor });
  drawIcon(btn.iconGfx, ICONS[iconName], theme.actionIconColor, ACTION_ICON_SIZE, 2.2);
}

function createActionButton(x: number, bgColor: number, iconName: IconName, theme: Theme): ActionButton {
  const container = new Container();
  container.x = x;
  container.y = 0;
  container.eventMode = 'static';
  container.cursor    = 'pointer';

  const graphics = new Graphics();
  container.addChild(graphics);

  const iconGfx = new Graphics();
  container.addChild(iconGfx);

  const btn = { container, graphics, iconGfx };
  renderActionButton(btn, bgColor, iconName, theme);
  return btn;
}

// ─── Status bar (4 aligned buttons above the circle) ─────────────────────────

export const STATUS_BTN_INACTIVE_ALPHA = 0.35;
export const STATUS_BTN_ACTIVE_ALPHA   = 1.0;

function applyStatusActiveAlpha(bar: StatusBar, current: Status): void {
  for (let i = 0; i < STATUS_CYCLE.length; i++) {
    const btn = bar.buttons[i];
    if (!btn) continue;
    btn.container.alpha = STATUS_CYCLE[i] === current
      ? STATUS_BTN_ACTIVE_ALPHA
      : STATUS_BTN_INACTIVE_ALPHA;
  }
}

function createStatusBar(layout: NodeLayout, theme: Theme): StatusBar {
  const container = new Container();
  container.x = 0;
  container.y = -(layout.radius + ACTION_OFFSET_Y);
  container.visible = false;

  const spacing = ACTION_RADIUS * 2 + ACTION_GAP;
  const buttons = STATUS_CYCLE.map((status, i) => {
    const x = (i - (STATUS_CYCLE.length - 1) / 2) * spacing;
    const btn = createActionButton(
      x,
      theme.statusColors[status],
      STATUS_BUTTON_ICON[status],
      theme,
    );
    container.addChild(btn.container);
    return btn;
  });

  const bar: StatusBar = { container, buttons };
  applyStatusActiveAlpha(bar, layout.status);
  return bar;
}

/** Refresh visuals tied to `layout.status` (badge + active-button highlight). */
export function refreshNodeStatus(sprite: NodeSprite, theme: Theme): void {
  renderStatusBadge(sprite.statusBadge, sprite.layout, theme);
  applyStatusActiveAlpha(sprite.statusBar, sprite.layout.status);
}

/** Refresh the description badge after `layout.description` changes. */
export function refreshNodeDescription(sprite: NodeSprite, theme: Theme): void {
  renderDescBadge(sprite.descBadge, sprite.layout, theme);
}

function createActionBar(layout: NodeLayout, theme: Theme): ActionBar {
  const container = new Container();
  container.x = 0;
  container.y = layout.radius + ACTION_OFFSET_Y;
  container.visible = false;

  // 3 buttons centered at x = -spacing, 0, +spacing.
  const spacing = ACTION_RADIUS * 2 + ACTION_GAP;

  const addButton    = createActionButton(-spacing, theme.addColor,    'plus',   theme);
  const rotateButton = createActionButton(0,        theme.rotateColor, 'rotate', theme);
  const deleteButton = createActionButton( spacing, theme.deleteColor, 'trash',  theme);

  container.addChild(addButton.container);
  container.addChild(rotateButton.container);
  container.addChild(deleteButton.container);

  return { container, addButton, rotateButton, deleteButton };
}

// ─── Sprite creation ─────────────────────────────────────────────────────────

export function createNodeSprite(layout: NodeLayout, theme: Theme): NodeSprite {
  const container = new Container();
  container.x = layout.x;
  container.y = layout.y;

  const gfx = new Graphics();
  renderCircle(gfx, layout, theme);
  container.addChild(gfx);

  const text = new Text({ text: layout.label, style: makeTextStyle(layout, theme) });
  text.anchor.set(0.5, 0.5);
  container.addChild(text);

  const statusBadge = createStatusBadge(layout, theme);
  container.addChild(statusBadge.container);

  const descBadge = createDescBadge(layout, theme);
  container.addChild(descBadge.container);

  const statusBar = createStatusBar(layout, theme);
  container.addChild(statusBar.container);

  const actionBar = createActionBar(layout, theme);
  container.addChild(actionBar.container);

  return { container, graphics: gfx, text, layout, statusBadge, descBadge, statusBar, actionBar };
}

// ─── Theme switch ─────────────────────────────────────────────────────────────

export function applyThemeToSprites(
  sprites: Map<string, NodeSprite>,
  theme:   Theme,
): void {
  // Per-call TextStyle cache — most sprites share identical (fontSize, weight)
  // by virtue of the level → radius mapping (only ~4 distinct styles). PIXI
  // dedupes text textures by structural style, so reusing the same instance
  // also means a single rasterization pass.
  const textStyleCache = new Map<string, TextStyle>();
  const styleFor = (layout: NodeLayout): TextStyle => {
    const fontSize  = Math.max(10, Math.min(16, Math.round(layout.radius * 0.28)));
    const weight    = layout.level === 0 ? '700' : '600';
    const wrapWidth = (layout.radius - 11) * 2;
    const key       = `${fontSize}|${weight}|${wrapWidth}|${theme.textColor}`;
    let s = textStyleCache.get(key);
    if (!s) {
      s = makeTextStyle(layout, theme);
      textStyleCache.set(key, s);
    }
    return s;
  };

  for (const sprite of sprites.values()) {
    renderCircle(sprite.graphics, sprite.layout, theme);
    sprite.text.style = styleFor(sprite.layout);
    renderStatusBadge(sprite.statusBadge, sprite.layout, theme);
    renderDescBadge(sprite.descBadge, sprite.layout, theme);
    for (let i = 0; i < STATUS_CYCLE.length; i++) {
      const status = STATUS_CYCLE[i];
      const btn    = sprite.statusBar.buttons[i];
      if (!status || !btn) continue;
      renderActionButton(btn, theme.statusColors[status], STATUS_BUTTON_ICON[status], theme);
    }
    applyStatusActiveAlpha(sprite.statusBar, sprite.layout.status);

    renderActionButton(sprite.actionBar.addButton,    theme.addColor,    'plus',   theme);
    renderActionButton(sprite.actionBar.rotateButton, theme.rotateColor, 'rotate', theme);
    renderActionButton(sprite.actionBar.deleteButton, theme.deleteColor, 'trash',  theme);
  }
}
